import { Injectable } from '@angular/core';
import {
  PageApiModel,
  PageDataService,
  PageVersionApiModel,
  PageVersionStatusApiModel,
  GenrePageDataService,
  GenrePageApiModel,
  // CollectionPageDataService,
} from '@cxb/editor-stb-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Page } from '../../../domain/page';
import { SimplePageVersion } from '../../../domain/simple-page-version';
import { PageType } from '../../../page-edit-screen/page-type';
import { DEFAULT_PAGINATION_LIMIT } from '../../app-constants';
import { apiErrorMessage } from '../../functions/general-api-error-handling';
import * as Action from './page-selector.actions';
import { Store } from '@ngrx/store';
import { CombinedAppState, RootAppState } from '../../../app.reducer';

@Injectable()
export class PageSelectorEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly pageDataService: PageDataService,
    private readonly genrePageDataService: GenrePageDataService,
    private readonly store: Store<CombinedAppState & RootAppState>
  ) {}

  public fetchPages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Action.fetchPages),
      withLatestFrom(this.store.select((state) => state.app.selectedTenant)),
      switchMap(([{ term, excludeDrafts }, selectedTenant]) =>
        forkJoin([
          this.pageDataService.filterPages(
            term,
            excludeDrafts ? PageVersionStatusApiModel.Published : null,
            DEFAULT_PAGINATION_LIMIT,
            undefined,
            selectedTenant?.id
          ),
          this.genrePageDataService.getGenrePages(selectedTenant?.id),
        ]).pipe(
          map(([pages, genrePages]) =>
            Action.fetchPagesSuccess({
              pages: [
                ...pages.map((it) => this.mapPage(it, PageType.content)),
                ...genrePages.map((it) => this.mapPage(it, PageType.genre)),
              ],
            })
          ),
          catchError((error) => of(Action.fetchPagesFail(apiErrorMessage(error))))
        )
      )
    )
  );

  private mapPage(page: PageApiModel | GenrePageApiModel, pageType: PageType): Page {
    return {
      id: page.id,
      lastPublishedAt: new Date(page.lastPublishedAt).getTime(),
      pageVersions: page.pageVersions.map((pageVersion) => this.mapPageVersion(pageVersion)),
      pageUrl: undefined,
      type: pageType,
      genre: isGenrePageApiModel(page)
        ? {
            id: page.genre.id,
            guid: page.genre.guid,
            title: page.genre.title,
          }
        : undefined,
    };
  }

  private mapPageVersion(pageVersion: PageVersionApiModel): SimplePageVersion {
    return {
      id: pageVersion.id,
      internalName: pageVersion.internalName,
      titleEnglish: pageVersion.nameLocalized.en,
      titleArabic: pageVersion.nameLocalized.ar,
      status: pageVersion.status === PageVersionStatusApiModel.Draft ? 'DRAFT' : 'PUBLISHED',
    };
  }
}

const isGenrePageApiModel = (page: PageApiModel): page is GenrePageApiModel => {
  return 'genre' in page;
};
